/**
 * FIV-499 cookie-consent state. One first-party cookie is the single source of
 * truth for an EXPLICIT choice; absence (or a stale version) means "no choice
 * yet" — the banner shows, and getEffectiveConsent() resolves what may load:
 * nothing non-essential in opt-in regions (EEA/UK/CH), regional tracking-on
 * defaults elsewhere. See
 * docs/superpowers/specs/2026-07-20-fiv-499-cookie-banner-design.md and
 * docs/superpowers/specs/2026-07-26-fiv-499-consent-region-defaults-design.md
 */
import { getConsentRegion, hasGpcSignal } from "./region";
import { isAppContext } from "@/lib/appContext";

const COOKIE_NAME = "fs_cookie_consent";
export const CONSENT_VERSION = 1;
const MAX_AGE_S = 365 * 24 * 60 * 60; // 12 months

/** Dispatched on window to reopen the preferences modal from anywhere (footer links). */
export const PREFS_OPEN_EVENT = "fs-cookie-prefs-open";

export interface ConsentState {
  v: number;
  functional: boolean;
  advertising: boolean;
  ts: number;
}

type Listener = (c: ConsentState) => void;
const listeners = new Set<Listener>();

export function getConsent(): ConsentState | null {
  if (typeof document === "undefined") return null;
  try {
    const m = document.cookie.match(/(?:^|;\s*)fs_cookie_consent=([^;]*)/);
    if (!m) return null;
    const parsed = JSON.parse(decodeURIComponent(m[1])) as ConsentState;
    if (parsed.v !== CONSENT_VERSION) return null; // policy changed → re-prompt
    if (typeof parsed.functional !== "boolean" || typeof parsed.advertising !== "boolean") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasChoice(): boolean {
  return getConsent() !== null;
}

export interface EffectiveConsent {
  functional: boolean;
  advertising: boolean;
}

/** FIV-499 region defaults: with no stored choice, opt-out regions (non-
 *  EEA/UK/CH) default to tracking-on — the banner is their opt-out — while
 *  opt-in regions stay all-denied. GPC denies Advertising by default; an
 *  explicit stored choice always wins. Use hasChoice() to distinguish an
 *  explicit choice from regional defaults. */
export function getEffectiveConsent(): EffectiveConsent {
  if (isAppContext()) return { functional: false, advertising: false };
  if (typeof document === "undefined") {
    return { functional: false, advertising: false }; // no-DOM contexts match getConsent()'s denied posture
  }
  const stored = getConsent();
  if (stored) {
    return { functional: stored.functional, advertising: stored.advertising };
  }
  if (getConsentRegion() === "opt-in") {
    return { functional: false, advertising: false };
  }
  return { functional: true, advertising: !hasGpcSignal() };
}

export function hasFunctionalConsent(): boolean {
  return getEffectiveConsent().functional;
}

export function hasAdvertisingConsent(): boolean {
  return getEffectiveConsent().advertising;
}

export function setConsent(functional: boolean, advertising: boolean): ConsentState {
  if (isAppContext()) return { v: CONSENT_VERSION, functional: false, advertising: false, ts: Date.now() };
  const state: ConsentState = { v: CONSENT_VERSION, functional, advertising, ts: Date.now() };
  // Domain-scope to .fivestar.video in prod so future subdomains can share the
  // choice; host-only on localhost. Secure only over https (dev server is https,
  // jsdom tests are http).
  const domain =
    typeof location !== "undefined" &&
    (location.hostname === "fivestar.video" || location.hostname.endsWith(".fivestar.video"))
      ? "; Domain=.fivestar.video"
      : "";
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  try {
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(state))}` +
      `; Max-Age=${MAX_AGE_S}; Path=/${domain}; SameSite=Lax${secure}`;
  } catch {
    // SecurityError in cookie-blocked contexts must not throw
  }
  for (const l of listeners) {
    try {
      l(state);
    } catch {
      // one broken listener must not block the rest or the caller
    }
  }
  return state;
}

export function onConsentChange(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Reopen the Cookie Preferences modal (used by footer links). */
export function openCookiePreferences(): void {
  if (isAppContext()) return;
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PREFS_OPEN_EVENT));
}
